import { FC, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { useDebounce } from 'react-use';

import type { SearchResultsFooterReporter } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/SearchResultsFooterState';
import { SEARCH_HORIZONTAL_GUTTER } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchTabs';
import type { SearchMatchMode, SearchMode } from '@/queries';
import { AnimatedSearchSiderPanel, SearchSiderToggleRow, type SearchFilterChangeHandler } from '@/shared';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import { resolveSearchPreferences, SearchPreferences } from '../../../../preferences';
import AllSearchFilters from './components/AllSearchFilters';
import SearchResultsList from './components/SearchResultsList';
import { hasActiveSearchDomainFilters } from './searchCriteria';
import { ALL_SEARCH_DOMAINS, AllSearchDomain, useAllSearch } from './useAllSearch';

interface FederatedSearchViewProps {
    domains?: readonly AllSearchDomain[];
    filters: unknown;
    handleChange: SearchFilterChangeHandler;
    leftSiderCollapsed: boolean;
    matchMode: SearchMatchMode;
    onToggleLeftSider: () => void;
    onToggleRightSider: () => void;
    onResultsFooterChange: SearchResultsFooterReporter;
    query: string;
    rightSiderCollapsed: boolean;
    searchMode?: SearchMode;
}

export const FederatedSearchView: FC<FederatedSearchViewProps> = ({
    domains = ALL_SEARCH_DOMAINS,
    filters,
    handleChange,
    leftSiderCollapsed,
    matchMode,
    onToggleLeftSider,
    onToggleRightSider,
    onResultsFooterChange,
    query,
    rightSiderCollapsed,
    searchMode = 'keyword',
}): JSX.Element => {
    const { preferences } = useUserPreferences();
    const searchPreferences = resolveSearchPreferences(preferences?.search as SearchPreferences | undefined);
    const [debouncedQuery, setDebouncedQuery] = useState(query.trim());
    useDebounce(() => setDebouncedQuery(query.trim()), 350, [query]);
    const effectiveQuery = query.trim() ? debouncedQuery : '';
    const inputPending = query.trim() !== effectiveQuery;
    const { error, loading, loadingDomains, results } = useAllSearch({
        domains,
        filters,
        matchMode,
        query: effectiveQuery,
        searchMode,
    });
    const criteriaActive = hasActiveSearchDomainFilters(filters, domains);
    const resultsRequestKey = useMemo(
        () => JSON.stringify({ domains, filters, matchMode, query: effectiveQuery, searchMode }),
        [domains, effectiveQuery, filters, matchMode, searchMode],
    );

    return (
        <div
            style={{
                boxSizing: 'border-box',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                padding: `0 ${SEARCH_HORIZONTAL_GUTTER}`,
            }}
        >
            <Row gutter={[24, 0]} style={{ height: '100%', minHeight: 0 }} wrap={false}>
                <AnimatedSearchSiderPanel
                    open={!leftSiderCollapsed}
                    padding='0.5rem 0 0'
                    siderLabel='Search filters'
                    style={{ height: '100%', minHeight: 0 }}
                    width='20.833333%'
                >
                    <AllSearchFilters domains={domains} filters={filters} handleChange={handleChange} />
                </AnimatedSearchSiderPanel>
                <Col
                    flex='auto'
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0,
                        minWidth: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        paddingTop: '0.5rem',
                    }}
                >
                    <SearchSiderToggleRow
                        leftSiderCollapsed={leftSiderCollapsed}
                        onToggleLeftSider={onToggleLeftSider}
                        onToggleRightSider={onToggleRightSider}
                        rightSiderCollapsed={rightSiderCollapsed}
                    />
                    <div style={{ flex: '1 1 0%', minHeight: 0, overflowX: 'hidden', overflowY: 'auto' }}>
                        <SearchResultsList
                            key={resultsRequestKey}
                            criteriaActive={criteriaActive}
                            domains={domains}
                            error={error}
                            filters={filters}
                            groupRelatedListings={searchPreferences.results.groupRelatedListings}
                            handleChange={handleChange}
                            loading={loading || inputPending}
                            loadingDomains={inputPending ? domains : loadingDomains}
                            pageSize={searchPreferences.results.pageSize}
                            pending={inputPending}
                            query={effectiveQuery}
                            reportResultsFooter={onResultsFooterChange}
                            results={results}
                            showRecentSearches={searchPreferences.results.showRecentSearches}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

const AllSearch: FC<Omit<FederatedSearchViewProps, 'domains'>> = (props): JSX.Element => (
    <FederatedSearchView {...props} domains={ALL_SEARCH_DOMAINS} />
);

export default AllSearch;
